import React, { useContext, useState } from 'react';
import {
	Badge,
	Col,
	FormFeedback,
	FormGroup,
	FormText,
	Label,
} from 'reactstrap';
import Select from 'react-select';
import { t } from '../../utils/labels';
import { FieldComponent, StringListFieldType } from '../../types/form';
import { getLocalError } from '../../utils/formErrors';
import { FormContext } from '../../utils/context/form';

/**
 * Campo "lista di frasi libere": l'utente scrive una frase e con Invio la trasforma in un
 * chip rimovibile, mentre l'input resta disponibile per la successiva. Il valore del campo
 * e' un array di stringhe (la serializzazione JSON avviene nel beforeSaveTransformer).
 *
 * IMPORTANT - il menu a tendina e' disabilitato (menuIsOpen={false}): con il menu chiuso
 * react-select ignora l'Invio, quindi la creazione del chip la gestiamo noi in onKeyDown.
 * react-select richiama l'handler passato dall'esterno PRIMA del proprio e si ferma se e'
 * stato chiamato preventDefault().
 */
interface ChipOption {
	label: string;
	value: string;
}

const toOptions = (values: ReadonlyArray<string>): ChipOption[] =>
	values.map((v) => ({ label: v, value: v }));

export const StringListField: React.FC<FieldComponent<
	StringListFieldType
>> = ({
	field: {
		label,
		value,
		name,
		additionalHelpComponent,
		helpText,
		hidden,
		description,
		disabled,
	},
	changeHandler,
	path,
}) => {
	const formContext = useContext(FormContext);
	const error = getLocalError(path, formContext?.errors || {});
	const [inputValue, setInputValue] = useState('');

	const values: ReadonlyArray<string> = value || [];

	// Conferma il testo digitato: ignora il vuoto, toglie gli spazi ai lati, rifiuta i
	// duplicati (confronto case-insensitive: per l'editor "Plant Based" e "plant based"
	// sono la stessa voce).
	const commit = () => {
		const trimmed = inputValue.trim();
		if (!trimmed) {
			setInputValue('');
			return;
		}
		const isDuplicate = values.some(
			(v) => v.toLowerCase() === trimmed.toLowerCase(),
		);
		if (isDuplicate) {
			// IMPORTANT - il testo NON va cancellato: sparire senza aggiungere il chip
			// e senza un messaggio farebbe credere all'utente di aver perso quanto scritto
			return;
		}
		changeHandler(values.concat([trimmed]), path);
		setInputValue('');
	};

	return (
		<FormGroup row className={hidden ? 'hidden' : ''}>
			<Label sm={3}>{label}: </Label>
			<Col sm={9}>
				<Select
					classNamePrefix={name + '-select '}
					className={
						'input-border' + (Boolean(error) ? ' is-invalid' : '')
					}
					name={name}
					components={{ DropdownIndicator: null }}
					isMulti={true}
					isClearable={true}
					isDisabled={disabled}
					menuIsOpen={false}
					options={[]}
					value={toOptions(values)}
					inputValue={inputValue}
					onInputChange={(nextInput: string) => setInputValue(nextInput)}
					onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
						if (event.key === 'Enter') {
							// IMPORTANT - l'Invio va SEMPRE fermato, anche a campo vuoto:
							// senza preventDefault farebbe il submit implicito del form
							event.preventDefault();
							commit();
							return;
						}
						if (event.key === 'Tab') {
							// col campo vuoto il Tab deve passare, altrimenti il focus
							// resta intrappolato qui e la form non e' piu' navigabile
							if (!inputValue.trim()) {
								return;
							}
							event.preventDefault();
							commit();
						}
					}}
					// IMPORTANT - emetto SEMPRE un array (mai null): il submit di Form scarta
					// i valori null in creazione, quindi svuotare i chip non azzererebbe il campo
					onChange={(nextValue: any) =>
						changeHandler(
							nextValue
								? (nextValue as ReadonlyArray<ChipOption>).map(
										(option) => option.value,
								  )
								: [],
							path,
						)
					}
					placeholder={
						disabled ? '' : t`Type a phrase and press Enter to add it`
					}
				/>
				{error && (
					<FormFeedback invalid>
						{t(error.message + '/stringList', error.message)}
					</FormFeedback>
				)}
				<FormText color="muted">
					{helpText}
					{description.readonly && (
						<div>
							<Badge color="warning">{t`warning`}</Badge>{' '}
							{t`readonly field description`}
						</div>
					)}
					{additionalHelpComponent}
				</FormText>
			</Col>
		</FormGroup>
	);
};
